import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseApiClient } from '@app/core/services/base-api-client.service';
import { LoadingIndicatorService } from '@app/core/services/loading-indicator.service';
import { LogService } from '@app/core/services/log.service';
import { SDSGHSLabel, SDSNonGHSLabel } from '@app/sds/shared/models/sds-label';
import { Environment } from '@env/environment';
import { catchError, Observable, tap } from 'rxjs';

const httpOptions = {
	headers: new HttpHeaders({ responseType: 'blob', observe: 'response' }),
};
export interface IFileManagementService {
	url: string;
	downloadFileByDocId(docId: string): Observable<any>;
	downloadFileByFileId(fileId: number): Observable<any>;
	downloadFileByGHSLabel(request: SDSGHSLabel): Observable<any>;
	downloadFileByNonGHSLabel(request: SDSNonGHSLabel): Observable<any>;
}
@Injectable({
	providedIn: 'root',
})
export class FileManagementService extends BaseApiClient {
	/**
	 * The base URL for the API domain.
	 */
	public baseUrl: string;

	/**
	 * The URL to the API endpoint.
	 */
	public url: string;

	constructor(
		private readonly http: HttpClient,
		private readonly log: LogService,
		private readonly loadingIndicatorService: LoadingIndicatorService,
		environment: Environment
	) {
		super();
		this.baseUrl = environment.services['employee'];
		this.url = this.baseUrl + 'File';
	}

	/**
	 * Download File by document Id
	 * @param docId
	 */
	downloadFileByDocId(docId: string): Observable<any> {
		const url = `${this.url}/DownloadFileByDocId?docId=${docId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}
	/**
	 * Download File by File Id
	 * @param fileId
	 */
	downloadFileByFileId(fileId: number): Observable<any> {
		const url = `${this.url}/DownloadFileByFileId?fileId=${fileId}`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Download File by GHS Label
	 * @param fileId
	 */
	downloadFileByGHSLabel(request: SDSGHSLabel): Observable<any> {
		const url = `${this.url}/GHSlabel`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.post(url, request, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Download File by Non GHS Label
	 * @param fileId
	 */
	downloadFileByNonGHSLabel(request: SDSNonGHSLabel): Observable<any> {
		const url = `${this.url}/NonGHSLabel`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.post(url, request, { observe: 'response', responseType: 'blob' }).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}

	/**
	 * Upload Audit Attachment For Question
	 * @param file
	 */
	uploadAuditFile(file: FormData): Observable<string> {
		const url = `${this.url}/UploadAuditAttachment`;
		const errorHandler = this.createErrorResponseHandler(this.log, undefined, (_) => this.loadingIndicatorService.stopLoading());
		this.loadingIndicatorService.startLoading();
		return this.http.post<string>(url, file, httpOptions).pipe(
			tap((result) => {
				this.loadingIndicatorService.stopLoading();
			}),
			catchError(errorHandler)
		);
	}
}
